import { ProcessEntity } from '@common/enums/processEntity';
import { CpapOption } from '@common/validation/customControls/somnology';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import type { IKsyosMedicalCenterCustomControlDataFields } from './Osasc2V2KsyosMedicalCenterCustomControlFields';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { isCustomControlComplete } from '../../../../../utils/isCustomControlDataComplete';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import {
    ksyosMedicalCenterCustomControlDataFieldsInitialize,
    Osasc2V2KsyosMedicalCenterCustomControlFields,
} from './Osasc2V2KsyosMedicalCenterCustomControlFields';

interface ICustomControlData {
    fields: IKsyosMedicalCenterCustomControlDataFields | null;
    somnologyOrganizationType: SomnologyOrganizationType | null;
}

interface IInnerOsasc2V2DiagnosticReportAddEditState {
    buttonDisabled?: boolean;
    therapieLandEmailOrRightsError?: boolean;
}

export function Osasc2V2DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    return <InnerOsasc2V2DiagnosticReportAddEdit {...props} />;
}

export class InnerOsasc2V2DiagnosticReportAddEdit extends Component<
    ICustomControlDiagnosticReportAddEditProps,
    IInnerOsasc2V2DiagnosticReportAddEditState
> {
    public async componentDidMount() {
        if (!this.props.diagnosticReport.customControlData.somnologyOrganizationType) {
            await this.props.handleChangeCustomControl({
                somnologyOrganizationType: this.props.request.customControlData.somnologyOrganizationType,
            });
            if (
                this.props.request.customControlData.somnologyOrganizationType ===
                SomnologyOrganizationType.ksyosMedicalCenter
            ) {
                await this.props.handleChangeCustomControl({
                    fields: ksyosMedicalCenterCustomControlDataFieldsInitialize(),
                });
            }
        } else {
            const somnologyOrganizationType = this.props.request.customControlData.somnologyOrganizationType;
            const customControlData = this.props.diagnosticReport.customControlData;
            let updateCustomControlData;

            if (somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter) {
                const initializedCustomControlData = ksyosMedicalCenterCustomControlDataFieldsInitialize();
                const initializedBeleid = initializedCustomControlData.beleid;
                const initializedToesturen = initializedCustomControlData.toesturen;
                const initializedComorbiditeitenVolgensHuisarts =
                    initializedCustomControlData.comorbiditeitenVolgensHuisarts;
                updateCustomControlData = {
                    fields: {
                        ...initializedCustomControlData,
                        ...customControlData.fields,
                        beleid: {
                            ...initializedBeleid,
                            ...customControlData.fields.beleid,
                        },
                        toesturen: {
                            ...initializedToesturen,
                            ...customControlData.fields.toesturen,
                        },
                        comorbiditeitenVolgensHuisarts: {
                            ...initializedComorbiditeitenVolgensHuisarts,
                            ...customControlData.fields.comorbiditeitenVolgensHuisarts,
                        },
                    },
                };
            }

            if (updateCustomControlData) {
                await this.props.handleChangeCustomControl(updateCustomControlData);
            }
        }
    }

    public render() {
        const somnologyOrganizationType = this.props.request.customControlData.somnologyOrganizationType;
        const customControlData = this.props.diagnosticReport.customControlData;
        let complete = false;

        let initializedCustomControlData;
        if (somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter) {
            initializedCustomControlData = ksyosMedicalCenterCustomControlDataFieldsInitialize();
            complete =
                isCustomControlComplete(initializedCustomControlData, customControlData.fields) &&
                isCustomControlComplete(initializedCustomControlData.beleid, customControlData.fields.beleid) &&
                isCustomControlComplete(
                    initializedCustomControlData.comorbiditeitenVolgensHuisarts,
                    customControlData.fields.comorbiditeitenVolgensHuisarts
                );
        }

        if (!complete) {
            return null;
        }

        return (
            <ProcessContainer>
                <ProcessItemHeader
                    isFirstItem
                    isItemOpen
                    organizationName={this.props.currentDiagnosticReport?.performerOrganization?.name}
                    practitionerName={
                        this.props.currentDiagnosticReport && this.props.currentDiagnosticReport.performerPractitioner
                            ? getDisplayName(this.props.currentDiagnosticReport.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    serviceType={ServiceType.consultation}
                    title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
                />

                <Form onSubmit={() => null}>
                    {this.props.diagnosticReport.customControlData.fields && (
                        <>
                            {this.props.request.customControlData.somnologyOrganizationType ===
                                SomnologyOrganizationType.ksyosMedicalCenter && (
                                <Osasc2V2KsyosMedicalCenterCustomControlFields
                                    {...this.props}
                                    fields={this.props.diagnosticReport.customControlData.fields}
                                    remark={this.props.diagnosticReport.remark}
                                    setButtonDisabled={async (errorShown: boolean) =>
                                        await this.setState({ therapieLandEmailOrRightsError: errorShown })
                                    }
                                />
                            )}
                        </>
                    )}
                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.diagnosticReport}
                                isExisting={!!this.props.request.id}
                                revertChanges={this.props.revertDiagnosticReportChanges}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveDiagnosticReport('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled || this.state.therapieLandEmailOrRightsError}
                                saveSend={async () => {
                                    await this.saveDiagnosticReport('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </ProcessContainer>
        );
    }

    private async saveDiagnosticReport(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedDiagnosticReport = await this.props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const diagnosticReportId = await this.props.onSaveDiagnosticReport(saveType);
        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'request',
                translateKey('toast.answerSentToOrganizationSuccess', this.props.request.requesterOrganization?.name)
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await this.setState({ buttonDisabled: false });
        await this.props.onSave(diagnosticReportId);
        if (this.props.diagnosticReport.customControlData.fields.osasq_resend_date) {
            await this.props.reloadProcess(ReloadEpisode.reloadAll);
        }
    }
}

function validateBeleid(customControlData: ICustomControlData, errors: ValidationErrors) {
    if (
        customControlData.somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter &&
        customControlData.fields?.osa
    ) {
        const beleidFilledIn = Object.values(customControlData.fields.beleid).some((value) => {
            return value !== false && value !== null;
        });

        errors = beleidFilledIn
            ? errors.remove('customControlData.fields.beleid')
            : errors.set('customControlData.fields.beleid', 'any_required');

        if (customControlData.fields.beleid.cpap) {
            const cpapOptionValues: string[] = Object.values(CpapOption);
            if (
                customControlData.fields.beleid.cpapOption &&
                cpapOptionValues.includes(customControlData.fields.beleid.cpapOption)
            ) {
                errors = errors.remove('customControlData.fields.beleid.cpapOption');
            } else {
                errors = errors.set('customControlData.fields.beleid.cpapOption', 'any_required');
            }
        } else {
            errors = errors.remove('customControlData.fields.beleid.cpapOption');
        }
    } else {
        errors = errors.remove('customControlData.fields.beleid');
        errors = errors.remove('customControlData.fields.beleid.cpapOption');
    }

    return errors;
}

export function osasc2V2DiagnosticReportValidateCustomControlData(
    customControlData: ICustomControlData,
    errors: ValidationErrors
) {
    if (customControlData.fields) {
        errors = validateBeleid(customControlData, errors);
    }

    return errors;
}

export function osasc2V2DiagnosticReportValidateOneCustomControlData(
    name: string,
    customControlData: ICustomControlData,
    errors: ValidationErrors
) {
    if (customControlData.fields && name === 'fields.beleid') {
        return validateBeleid(customControlData, errors);
    }

    return errors;
}

export function osasc2V2DiagnosticReportInitialize() {
    return {
        somnologyOrganizationType: null,
        fields: null,
    };
}
